import React, { useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";
import { startLevelMeter } from "@/lib/orbLevel";

/* One-way voice note: MediaRecorder → /api/stt/transcribe (whisper-1) → editable chat input. Never auto-sends. */
export const VoiceNote = ({ lang, onTranscript, setOrbState }) => {
  const { tx } = useI18n();
  const [status, setStatus] = useState(null);
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [secs, setSecs] = useState(0);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const meterStopRef = useRef(null);

  useEffect(() => { api.get("/stt/status").then((r) => setStatus(r.data)).catch(() => {}); }, []);
  useEffect(() => () => { clearInterval(timerRef.current); meterStopRef.current?.(); streamRef.current?.getTracks().forEach((t) => t.stop()); }, []);

  const start = async () => {
    if (!status?.available) { toast.error(status?.message || tx("Sesli not kullanılamıyor")); return; }
    if (!window.MediaRecorder) { toast.error(tx("Bu tarayıcı ses kaydını desteklemiyor")); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = upload;
      rec.start(250);
      recRef.current = rec;
      meterStopRef.current = startLevelMeter(stream);
      setRecording(true); setSecs(0); setOrbState?.("listening");
      timerRef.current = setInterval(() => setSecs((s) => {
        if (s + 1 >= 120) { stop(); }
        return s + 1;
      }), 1000);
    } catch (e) {
      toast.error(tx("Mikrofon izni alınamadı"));
    }
  };

  const stop = () => {
    clearInterval(timerRef.current);
    meterStopRef.current?.(); meterStopRef.current = null;
    try { recRef.current?.state !== "inactive" && recRef.current?.stop(); } catch (e) {}
    streamRef.current?.getTracks().forEach((t) => t.stop());
    setRecording(false);
  };

  const upload = async () => {
    const blob = new Blob(chunksRef.current, { type: "audio/webm" });
    if (blob.size < 1000) { toast.error(tx("Kayıt çok kısa")); setOrbState?.("idle"); return; }
    setBusy(true); setOrbState?.("thinking");
    try {
      const fd = new FormData();
      fd.append("file", blob, "voice-note.webm");
      fd.append("language", lang === "en" ? "en" : "tr");
      const r = await api.post("/stt/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (!r.data.text) { toast.info(tx("Konuşma algılanmadı — metin boş")); }
      else { onTranscript(r.data.text); toast.success(tx("Yazıya çevrildi — düzenleyip Gönder'e basın")); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || tx("Yazıya çevrilemedi"));
    } finally { setBusy(false); setOrbState?.("idle"); }
  };

  return (
    <div className="flex flex-col gap-1" data-testid="voice-note">
      {!recording ? (
        <button data-testid="voice-note-start-btn" onClick={start} disabled={busy || status === null}
          title={tx("Sesli not: ses → metin (tek yönlü, GPT-Live değil)")}
          className={`self-start whitespace-nowrap flex items-center gap-2 px-3 py-2 rounded-full border text-sm ${status?.available ? "bg-white/5 border-white/15 text-white/80 hover:border-cyan-400/40 hover:text-cyan-200" : "bg-white/5 border-white/10 text-white/40"} disabled:opacity-50`}>
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Mic size={16} />} {busy ? tx("Yazıya çevriliyor…") : tx("Sesli not")}
        </button>
      ) : (
        <button data-testid="voice-note-stop-btn" onClick={stop}
          className="self-start whitespace-nowrap flex items-center gap-2 px-3 py-2 rounded-full border text-sm bg-red-500/20 border-red-400/40 text-red-200 animate-pulse">
          <Square size={14} /> {tx("Kaydı bitir · {s}s", { s: secs })}
        </button>
      )}
      <span className="text-[0.62rem] text-white/40 leading-tight" data-testid="voice-note-note">{status?.available ? tx("Metin önce girişe gelir; siz Gönder'e basarsınız.") : (status?.message || tx("Durum alınıyor…"))}</span>
    </div>
  );
};
